/**
 * Grab-bag of one-off query/mutation hooks for straggler call sites that
 * don't belong to an existing queries/*.js file's topic (webhooks, agents,
 * billing, tasks, ...). Each hook here is used from exactly one call site;
 * follows src/queries/webhooks.js's template.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { request, ReqMethods } from "../lib/apiClient";

const { GET, POST } = ReqMethods;

// SSOIframe's post-SSO "check_location" side effect (webhook/check_location).
// Fired from a useEffect reacting to SSO data, not rendered off a cache key,
// so it's a mutation like webhooks.js's useWebhookConfigSecret.
export function useCheckLocation() {
  return useMutation({
    mutationFn: (payload) => request(POST, "/webhook/check_location", { payload }),
  });
}

// billing/component/ViewModel's "Subscribe now" confirm action.
export function useSubscribePlan() {
  return useMutation({
    mutationFn: (payload) => request(POST, "/accounts/subscribplan", { payload }),
  });
}

// Settings > Wallet's transaction-history list (GET /agency/chargelogs).
// `enabled: false`: the caller accumulates pages into its own logs/credits
// state (classic infinite-scroll append-by-page), so it drives fetching
// imperatively via `refetch()` from an effect keyed on
// [userActiveLocation, page], exactly like the original raw fetch did,
// rather than rendering off `data` directly.
export function useAgencyChargelogs({ accountId, locationId, page, limit }) {
  return useQuery({
    queryKey: ["agencyChargelogs", accountId, locationId, page, limit],
    queryFn: () =>
      request(GET, "/agency/chargelogs", {
        query: `account_id=${accountId}&accountId=${locationId}&level=account&page=${page}&limit=${limit}`,
      }),
    enabled: false,
  });
}

// FreeTrail (orphan signup page)'s handleSubmit.
export function useCreateBusinessAccount() {
  return useMutation({
    mutationFn: (payload) => request(POST, "/accounts/create_busines_account", { payload }),
  });
}

// Home's newsfeed strip (GET /feeds/filter?agency_id=...), fired once on
// mount exactly like the original useEffect(() => { getNewsFeed() }, []) —
// keyed by agencyId so it still refetches if that ever changes underneath.
export const newsfeedKeys = {
  all: ["newsfeed"],
  byAgency: (agencyId) => [...newsfeedKeys.all, agencyId],
};

export function useNewsfeedByAgency(agencyId) {
  return useQuery({
    queryKey: newsfeedKeys.byAgency(agencyId),
    queryFn: async () => {
      const res = await request(GET, "/feeds/filter", { query: `agency_id=${agencyId}` });
      return res?.data ?? [];
    },
  });
}

// WorkspaceSwitcher's "Add Workspace +" template lookup (handleNavigate),
// fired on click, not on mount.
export function useFreeAccountTemplates() {
  return useMutation({
    mutationFn: (agencyId) =>
      request(GET, "/free_account_template", { query: `agency_id=${agencyId}&page=1&limit=100` }),
  });
}
